import { CancelablePromise } from 'cancelable-promise';
import { stringify } from "./querystring";
import { CancellablePromise } from "../interfaces/api";
import EventEmitter, { EventEmitterCallback } from "./EventEmitter";

const { API_URL } = window.ENV;

let _token: string | null = null;
let _apiUrl = API_URL;
export const setToken = (token: string | null) => _token = token;
export const setApiUrl = (apiUrl: string) => _apiUrl = apiUrl;

const fetchEmitter = new EventEmitter();
export const onError = (onErrorCallback: EventEmitterCallback<IFetchError>) => fetchEmitter.on('error', onErrorCallback);
export const onSuccess = (onSuccessCallback: EventEmitterCallback<ElsaXMLHttpRequest>) => fetchEmitter.on('success', onSuccessCallback);

export type IFetchMethod = 'GET' | 'DELETE' | 'POST' | 'PUT' | 'PATCH';
export type ElsaXMLHttpRequest<R = any> = XMLHttpRequest & {
  elsa: {
    method: IFetchMethod,
    url: string,
    body: IFetchBody,
    config: IFetchConfig | IFetchConfigWithRaw,
    response: R,
  }
};
export const FETCH_NETWORK_ERROR = 'Network Error';
export const FETCH_ABORTED_ERROR = 'Request aborted';
export const FETCH_DEFAULT_RETRY_DELAY = 5000;
export const FETCH_UNAVAILABLE_STATUS = 503;

export type IFetchConfigWithoutRaw = {
  headers?: { [headerName: string]: string },
  onUploadProgress?: ({ loaded, total }: { loaded: number, total: number }) => any,
  onDownloadProgress?: ({ loaded, total }: { loaded: number, total: number }) => any,
  requestType?: "form" | "json",
  responseType?: "blob" | "json" | "text",
  networkErrorRetry?: number,
}

export type IFetchConfigWithRaw = IFetchConfigWithoutRaw & {
  rawResponse: true
}

export type IFetchConfig = IFetchConfigWithoutRaw | IFetchConfigWithRaw

export type IFetchBody = Record<string, any>;

export class IFetchError<E = object> extends Error {
  method: IFetchMethod;
  url: string;
  body: IFetchBody | string;
  status: number;
  error: E;

  constructor() {
    super('fetch error');
    this.name = "IFetchError";
  }
}

// export type IFetchError<E = object> = { url: string, status: number, error: E };

function fetch<R = any>(
  method: IFetchMethod,
  url: string,
  body: IFetchBody,
  config: IFetchConfigWithRaw,
): CancellablePromise<ElsaXMLHttpRequest<R>>;

function fetch<R = any>(
  method: IFetchMethod,
  url: string,
  body?: IFetchBody,
  config?: IFetchConfigWithoutRaw,
): CancellablePromise<R>;

function fetch<R = any>(
  method: IFetchMethod,
  url: string,
  body?: IFetchBody,
  config: IFetchConfig = {},
) {
  let _resolve: (props: R | ElsaXMLHttpRequest) => any;
  let _reject: (err: IFetchError) => any;
  let _request: ElsaXMLHttpRequest;
  let _aborted: boolean = false;

  const fetchError = new IFetchError();
  fetchError.method = method;
  fetchError.body = body;
  fetchError.url = url;

  const paramsInUrl = isParamsInUrl(method);
  url = buildUrl(url, paramsInUrl ? body : null);

  const headers: Record<string, string> = {
    'Accept': 'application/json, text/plain, */*',
    ...config.headers,
  }
  if (_token) headers.Authorization = `Bearer ${_token}`;

  const responseType = config.responseType;

  const sendBody = paramsInUrl ? null : getRequestSend(body, headers, config.requestType) || null;

  const { networkErrorRetry } = config;
  const errorRetry: number =
    typeof networkErrorRetry === 'number'
      ? networkErrorRetry
      : FETCH_DEFAULT_RETRY_DELAY;

  const sendNetworkError = (status = -1, body: string | object = FETCH_NETWORK_ERROR) => {
    // ошибка создается до повторного запроса, чтобы сработал eventEmitter внутри createError
    const error = completeError(fetchError, status, body);
    if (errorRetry) {
      fetchEmitter.emit('error', error);
      return setTimeout(createRequest, errorRetry);
    }
    return rejectRequest(error);
  }

  function onloadend() {
    if (!_request) return;
    const request = _request;
    _request = null;
    const { status } = request;
    if (status === 0 && !request.response) {
      sendNetworkError();
    } else if (status === FETCH_UNAVAILABLE_STATUS) {
      const response = parseResponse(request, responseType);
      sendNetworkError(FETCH_UNAVAILABLE_STATUS, response);
    } else {
      try {
        const response = parseResponse(request, responseType);
        if (validateStatus(status)) {
          request.elsa.response = response;
          fetchEmitter.emit('success', request);
          _resolve(isRawResponse(config) ? request : response);
        } else {
          rejectRequest(completeError(fetchError, status, response));
        }
      } catch (e) {
        rejectRequest(completeError(fetchError, status, e.message))
      }
    }
  }

  function rejectRequest(error: IFetchError) {
    fetchEmitter.emit('error', error);
    _reject(error);
  }

  function onabort() {
    if (!_request) return;
    _request = null;
    rejectRequest(completeError(fetchError, -1, FETCH_ABORTED_ERROR));
  }

  function onerror() {
    if (!_request) return;
    _request = null;
    sendNetworkError();
  }

  const createRequest = () => {
    if (_aborted) return;
    _request = new XMLHttpRequest() as ElsaXMLHttpRequest;
    _request.elsa = {
      method,
      url,
      body,
      config,
      response: null
    };
    _request.open(method, url)

    for (let key in headers) {
      _request.setRequestHeader(key, headers[key]);
    }
    if (responseType && responseType !== 'json') _request.responseType = config.responseType;

    if (typeof config.onDownloadProgress === 'function') {
      _request.addEventListener('progress', config.onDownloadProgress);
    }

    // Not all browsers support upload events
    if (typeof config.onUploadProgress === 'function' && _request.upload) {
      _request.upload.addEventListener('progress', config.onUploadProgress);
    }

    _request.onloadend = onloadend;

    _request.onabort = onabort;

    _request.onerror = onerror;

    _request.send(sendBody);
  }

  return new CancelablePromise<any>((resolve, reject, onCancel) => {
    _resolve = resolve;
    _reject = reject;

    createRequest();

    onCancel(function onCancel() {
      if (_request) {
        _request.abort();
      } else {
        _aborted = true;
        rejectRequest(completeError(fetchError, -1, FETCH_ABORTED_ERROR));
      }
    });
  }) as unknown as CancellablePromise<any>;
}

const isParamsInUrl = (method: IFetchMethod) => method === 'GET' || method === 'DELETE';

const absoluteTest = /^https?:\/\//i;
const buildUrl = (url: string, body?: IFetchBody) => {
  if (!absoluteTest.test(url)) url = _apiUrl + '/api/' + url;
  if (body) {
    const params = stringify(body);
    if (params) url += '?' + params;
  }
  return url;
}

function isUndefined(val: any) {
  return typeof val === 'undefined';
}

function isObject(val: any) {
  return val !== null && typeof val === 'object';
}

function isFunction(val: any) {
  return toString.call(val) === '[object Function]';
}

function isFormData(val: any) {
  return (typeof FormData !== 'undefined') && (val instanceof FormData);
}

function isArrayBuffer(val: any) {
  return toString.call(val) === '[object ArrayBuffer]';
}

function isBuffer(val: any) {
  return val !== null && !isUndefined(val) && val.constructor !== null && !isUndefined(val.constructor)
    && typeof val.constructor.isBuffer === 'function' && val.constructor.isBuffer(val);
}

function isStream(val: any) {
  return isObject(val) && isFunction(val.pipe);
}

export function isBlob(val: any) {
  return toString.call(val) === '[object Blob]';
}

function isArrayBufferView(val: any) {
  var result;
  if ((typeof ArrayBuffer !== 'undefined') && (ArrayBuffer.isView)) {
    result = ArrayBuffer.isView(val);
  } else {
    result = (val) && (val.buffer) && (val.buffer instanceof ArrayBuffer);
  }
  return result;
}

function isURLSearchParams(val: any) {
  return typeof URLSearchParams !== 'undefined' && val instanceof URLSearchParams;
}

function setContentTypeIfUnset(headers: Record<string, string>, value: string) {
  if (isUndefined(headers['Content-Type'])) {
    headers['Content-Type'] = value;
  }
}

const getRequestSend = (data: any, headers: Record<string, string>, requestType: 'form' | 'json') => {
  if (requestType === 'form') {
    return serializeFormData(data);
  }
  if (isUndefined(data) || data === null) return null;
  headers['Content-Type'] = 'application/json';
  return JSON.stringify(data);
};

function validateStatus(status: number) {
  return !status || (status >= 200 && status < 300);
}

function completeError(fetchError: IFetchError, status: number, error: string | object): IFetchError {
  fetchError.status = status;
  fetchError.error = typeof error === 'string' ? { common: error } : error;
  fetchError.message = `${fetchError.status} ${fetchError.method} [${fetchError.status}]`;
  return fetchError;
}

function getResponseType(request: ElsaXMLHttpRequest, responseType: IFetchConfig['responseType']): 'json' | 'text' | null {
  if ('getResponseHeader' in request) {
    const contentType = request.getResponseHeader('content-type');
    if (contentType) return contentType.includes('application/json')
      ? 'json'
      : contentType.includes('text/html')
        ? 'text'
        : null;
  }
  if (responseType === 'text' || responseType === 'json') return responseType;
  return null;
}

export function parseResponse(request: ElsaXMLHttpRequest, responseType: IFetchConfig['responseType']) {
  const finalResponseType = getResponseType(request, responseType);
  if (finalResponseType === 'text') return request.responseText;
  if (finalResponseType === 'json') {
    const { responseType } = request;
    if (responseType === '' || responseType === 'text' || responseType === undefined)
      return JSON.parse(request.responseText)
  }
  return request.response;
}

function isFile(value: any) {
  return value instanceof File // web
    || (isObject(value) && value.hasOwnProperty('uri') && value.hasOwnProperty('type') && value.hasOwnProperty('name')); // RN
}

function serializeFormData(data: Record<string, any>): FormData {
  const formData = new FormData();
  const append = (name: string, value: any) => {
    if (value !== undefined && value !== null) formData.append(name, isFile(value) ? value : value + '');
  }
  if (data) {
    for (let key in data) {
      const value = data[key];
      if (Array.isArray(value)) {
        value.forEach(v => append(key, v));
      } else {
        append(key, value);
      }
    }
  }
  return formData;
}

function isRawResponse(config: any): config is IFetchConfigWithRaw {
  return config.rawResponse === true;
}

export const isElsaXMLHttpRequest = (response: any): response is ElsaXMLHttpRequest => response instanceof XMLHttpRequest && (response as any).elsa;

export default fetch;